<template>
  <div class="psychometrics-container">
    <div class="header">
      <h2>Psychometric Analysis</h2>
      <p class="subtitle">Select publications to include in your analysis*</p>
      <p class="subtitle-note">*Only publications in EXAM mode are eligible for psychometric analysis</p>
    </div>

    <!-- Global summary -->
    <div class="global-summary">
      <div class="summary-card total">
        <div class="icon">📊</div>
        <div class="content">
          <h3>{{ totalPublications }}</h3>
          <span>Available publications</span>
        </div>
      </div>
      <div class="summary-card participants">
        <div class="icon">👥</div>
        <div class="content">
          <h3>{{ totalParticipants }}</h3>
          <span>Total participants</span>
        </div>
      </div>
      <div class="summary-card selected">
        <div class="icon">✓</div>
        <div class="content">
          <h3>{{ selectedCount }}</h3>
          <span>Selected publications</span>
        </div>
      </div>
      <div class="summary-card sample">
        <div class="icon">🎯</div>
        <div class="content">
          <h3>{{ selectedParticipants }}</h3>
          <span>Analysis sample</span>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-section">
      <div class="loading-spinner"></div>
      <p>⏳ Loading publications...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-section">
      <p>❌ Error: {{ error }}</p>
      <button @click="loadPublications" class="btn btn-secondary">Retry</button>
    </div>

    <!-- Found publications -->
    <div v-else-if="publications.length > 0" class="publications-section">
      
      <!-- Quick actions -->
      <div class="quick-actions">
        <button @click="selectAll" class="btn btn-secondary">
          <span class="icon">☑️</span> Select all
        </button>
        <button @click="selectNone" class="btn btn-secondary">
          <span class="icon">☐</span> Deselect all
        </button>
        <button @click="selectRecent" class="btn btn-secondary">
          <span class="icon">🕐</span> Most recent
        </button>
        <button @click="selectLargest" class="btn btn-secondary">
          <span class="icon">📈</span> Largest samples
        </button>
      </div>

      <!-- Filters -->
      <div class="filters-section">
        <div class="filter-group">
          <label>Search:</label>
          <input 
            v-model="searchTerm" 
            type="text" 
            placeholder="Publication name..."
            class="search-input"
          >
        </div>
        <div class="filter-group">
          <label>Sort by:</label>
          <select v-model="sortBy" class="sort-select">
            <option value="date">Date (most recent)</option>
            <option value="participants">Number of participants</option>
            <option value="completion">Completion rate</option>
            <option value="name">Alphabetical name</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Minimum participants:</label>
          <input 
            v-model.number="minParticipants" 
            type="number" 
            min="1"
            class="number-input"
          >
        </div>
      </div>

      <!-- Publications list -->
      <div class="publications-list">
        <div 
          v-for="(publication, index) in filteredPublications" 
          :key="publication.id"
          class="publication-card"
          :class="{ 
            'selected': isSelected(publication.id),
            'recommended': publication.participantCount >= 30,
            'warning': publication.participantCount < 20
          }"
        >
          <!-- Card header -->
          <div class="card-header">
            <div class="selection-area">
              <input 
                type="checkbox" 
                :id="`pub-${publication.id}`"
                v-model="selectedPublications" 
                :value="publication.id"
                class="publication-checkbox"
              >
              <label :for="`pub-${publication.id}`" class="checkbox-label"></label>
            </div>
            
            <div class="publication-info">
              <h4>{{ publication.name }}</h4>
              <div class="meta-info">
                <span class="date">📅 {{ formatDate(publication.date) }}</span>
                <span class="context">📍 {{ publication.context }}</span>
                <span class="mode">⚙️ {{ publication.mode }}</span>
              </div>
            </div>
            
            <div class="quality-indicator">
              <div class="completion-badge" :class="getCompletionClass(publication.completionRate)">
                {{ publication.completionRate }}% completed
              </div>
            </div>
          </div>

          <!-- Card body -->
          <div class="card-body">
            <div class="stats-grid">
              <div class="stat-item">
                <span class="label">Participants:</span>
                <span class="value participants-count">{{ publication.participantCount }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Complete responses:</span>
                <span class="value">{{ publication.completeResponses }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Average time:</span>
                <span class="value">{{ publication.averageTime }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Institution:</span>
                <span class="value">{{ publication.institution }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Collection duration:</span>
                <span class="value">{{ publication.duration }}</span>
              </div>
              <div class="stat-item">
                <span class="label">Max attempts:</span>
                <span class="value">{{ publication.attempts }}</span>
              </div>
            </div>

            <!-- Quality indicators -->
            <div class="quality-indicators">
              <div class="indicator" :class="{ 'good': publication.participantCount >= 30, 'warning': publication.participantCount < 30 }">
                <span class="icon">👥</span>
                <span>{{ publication.participantCount >= 30 ? 'Sufficient sample' : 'Limited sample' }}</span>
              </div>
              <div class="indicator" :class="{ 'good': publication.completionRate >= 80, 'warning': publication.completionRate < 80 }">
                <span class="icon">✓</span>
                <span>{{ publication.completionRate >= 80 ? 'Good completion rate' : 'Completion needs improvement' }}</span>
              </div>
              <div class="indicator" :class="{ 'good': !publication.hasAnomalies, 'warning': publication.hasAnomalies }">
                <span class="icon">🔍</span>
                <span>{{ publication.hasAnomalies ? 'Anomalies detected' : 'Clean data' }}</span>
              </div>
            </div>
          </div>

          <!-- Card footer -->
          <div class="card-footer">
            <button @click="previewPublication(publication)" class="btn btn-link">
              <span class="icon">👁️</span> Data preview
            </button>
            <button @click="toggleSelection(publication.id)" class="btn btn-link">
              {{ isSelected(publication.id) ? '✓ Selected' : 'Select' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Data preprocessing -->
      <div v-if="selectedCount > 0" class="data-preprocessing">
        <div class="preprocessing-header">
          <h3>📊 Psychometric Data Preprocessing</h3>
          <p class="warning-text">
            <span class="icon">⚠️</span>
            Warning: Rigorous psychometric analysis requires prior data examination.
          </p>
        </div>

        <!-- Issues explanation -->
        <div class="methodology-section">
          <h4>🎯 Identified Methodological Issues</h4>
          <div class="issues-grid">
            <div class="issue-card multiple-attempts" :class="{ 'active': hasMultipleAttempts }">
              <div class="issue-icon">🔄</div>
              <div class="issue-content">
                <h5>Multiple Attempts</h5>
                <p>{{ multipleAttemptsCount }} students have made multiple attempts</p>
                <small>Impact: Learning effect, violation of independence of observations</small>
              </div>
            </div>
            
            <div class="issue-card incomplete-data" :class="{ 'active': hasIncompleteData }">
              <div class="issue-icon">⚠️</div>
              <div class="issue-content">
                <h5>Incomplete Data</h5>
                <p>{{ abandonedCount }} abandoned sessions detected</p>
                <small>Impact: Selection bias, reduced statistical power</small>
              </div>
            </div>
            
            <div class="issue-card sample-size" :class="{ 'active': hasSmallSample }">
              <div class="issue-icon">📈</div>
              <div class="issue-content">
                <h5>Sample Size</h5>
                <p>{{ selectedParticipants }} participants available</p>
                <small>Recommendation: N ≥ 100 for IRT analysis, N ≥ 30 for reliability</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Detailed statistics -->
        <div class="detailed-stats">
          <h4>📋 Detailed Numbers by Publication</h4>
          <div class="stats-table">
            <div class="table-header">
              <span>Publication</span>
              <span>Enrolled</span>
              <span>Participants</span>
              <span>Completed</span>
              <span>Abandoned</span>
              <span>Participation Rate</span>
              <span>Completion Rate</span>
            </div>
            <div 
              v-for="pub in selectedPublicationsData" 
              :key="pub.id"
              class="table-row"
            >
              <span class="pub-name">{{ pub.name }}</span>
              <span class="enrolled">{{ pub.enrolledStudents || '?' }}</span>
              <span class="participants">{{ pub.participantCount }}</span>
              <span class="completed">{{ pub.completeResponses }}</span>
              <span class="abandoned">{{ pub.abandonedSessions || 0 }}</span>
              <span class="participation-rate" :class="getRateClass(pub.participationRate)">
                {{ formatRate(pub.participationRate) }}%
              </span>
              <span class="completion-rate" :class="getRateClass(pub.completionRate)">
                {{ formatRate(pub.completionRate) }}%
              </span>
            </div>
            
            <!-- Totals row -->
            <div class="table-row total-row">
              <span><strong>TOTAL</strong></span>
              <span><strong>{{ totalEnrolled }}</strong></span>
              <span><strong>{{ selectedParticipants }}</strong></span>
              <span><strong>{{ totalCompleted }}</strong></span>
              <span><strong>{{ totalAbandoned }}</strong></span>
              <span><strong>{{ formatRate(overallParticipationRate) }}%</strong></span>
              <span><strong>{{ formatRate(overallCompletionRate) }}%</strong></span>
            </div>
          </div>
        </div>

        <!-- Filtering options -->
        <div class="filtering-options">
          <h4>🔧 Preprocessing Options</h4>
          <div class="filter-controls">
            <div class="filter-group">
              <label>
                <input type="checkbox" v-model="excludeMultipleAttempts">
                <span class="option-title">Exclude multiple attempts</span>
              </label>
              <small>Keep only the first attempt of each student</small>
            </div>
            
            <div class="filter-group">
              <label>
                <input type="checkbox" v-model="excludeIncomplete">
                <span class="option-title">Exclude incomplete responses</span>
              </label>
              <small>Remove abandoned sessions without final submission</small>
            </div>
            
            <div class="filter-group">
              <label>
                <input type="checkbox" v-model="excludeOutliers">
                <span class="option-title">Automatic outlier detection</span>
              </label>
              <small>Filter aberrant response times and suspicious patterns</small>
            </div>
          </div>
        </div>

        <!-- Final sample -->
        <div class="final-sample">
          <h4>🎯 Final Sample for Analysis</h4>
          <div class="sample-summary">
            <div class="sample-stat">
              <span class="label">Analyzable participants:</span>
              <span class="value" :class="getSampleQualityClass(finalSampleSize)">
                {{ finalSampleSize }} students
              </span>
            </div>
            <div class="sample-stat">
              <span class="label">Statistical power:</span>
              <span class="value" :class="getPowerClass(finalSampleSize)">
                {{ getPowerText(finalSampleSize) }}
              </span>
            </div>
            <div class="sample-stat">
              <span class="label">Recommended analyses:</span>
              <span class="value">{{ getRecommendedAnalyses(finalSampleSize) }}</span>
            </div>
          </div>
          
          <div class="quality-warnings" v-if="getQualityWarnings().length > 0">
            <h5>⚠️ Quality Warnings</h5>
            <ul>
              <li v-for="warning in getQualityWarnings()" :key="warning">{{ warning }}</li>
            </ul>
          </div>
        </div>

        <!-- Analysis configuration -->
        <div class="analysis-config">
          <div class="config-header">
            <h4>⚙️ Psychometric Analysis Configuration</h4>
          </div>
          
          <div class="config-options">
            <div class="option-group">
              <label>
                <input type="radio" v-model="analysisMode" value="individual">
                <span class="option-title">Separate analyses by publication</span>
              </label>
              <small>Compare psychometric properties between publications</small>
            </div>
            <div class="option-group">
              <label>
                <input type="radio" v-model="analysisMode" value="grouped">
                <span class="option-title">Grouped analysis (recommended)</span>
              </label>
              <small>Combine all data for a more robust global analysis</small>
            </div>
            <div class="option-group">
              <label>
                <input type="radio" v-model="analysisMode" value="comparative">
                <span class="option-title">Comparative analysis</span>
              </label>
              <small>Global analysis + comparisons between subgroups</small>
            </div>
          </div>

          <div class="analysis-actions">
            <button 
              @click="validateAndStartAnalysis" 
              class="btn btn-primary"
              :disabled="!canStartAnalysis"
              :class="{ 'btn-warning': hasQualityIssues }"
            >
              <span class="icon">{{ hasQualityIssues ? '⚠️' : '🚀' }}</span>
              {{ hasQualityIssues ? 'Launch with Warnings' : 'Launch Psychometric Analysis' }}
            </button>
            <button @click="exportPreprocessedData" class="btn btn-secondary">
              <span class="icon">💾</span>
              Export Preprocessed Data
            </button>
            <button @click="showMethodologyGuide" class="btn btn-link">
              <span class="icon">📚</span>
              Methodological Guide
            </button>
          </div>
        </div>
      </div>

      <!-- Analysis loading indicator -->
      <div v-if="analysisStarted && analysisResults?.loading" class="analysis-loading">
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <h3>🔬 Psychometric analysis in progress...</h3>
          <p>Computing reliability, difficulty and discrimination of items</p>
          <p><strong>Publication:</strong> {{ analysisResults.publicationName }}</p>
          <p><strong>Sample:</strong> {{ analysisResults.sampleSize }} participants</p>
        </div>
      </div>

      <!-- Psychometric Analysis Report -->
      <div v-if="analysisStarted && analysisResults && !analysisResults.loading" class="psychometric-report">
        <div class="report-header">
          <h2>📊 Psychometric Analysis Report</h2>
          <div class="report-meta">
            <span class="timestamp">Generated on {{ formatDateTime(analysisResults.timestamp) }}</span>
            <span class="sample-info">Sample: {{ analysisResults.sampleSize }} participants</span>
            <span class="publication-info">Publication: {{ analysisResults.publicationName }}</span>
          </div>
        </div>

        <!-- Tab navigation -->
        <div class="report-tabs">
          <button 
            @click="activeTab = 'synthesis'" 
            class="tab-button"
            :class="{ 'active': activeTab === 'synthesis' }"
          >
            📊 Synthesis View
          </button>
          <button 
            @click="activeTab = 'detailed'" 
            class="tab-button"
            :class="{ 'active': activeTab === 'detailed' }"
          >
            🔍 Detailed Analysis
          </button>
        </div>

        <!-- Synthesis view -->
        <div v-if="activeTab === 'synthesis'" class="synthesis-section">
          <div class="overall-grade-card">
            <div class="grade-display" :class="`grade-${analysisResults.overallGrade.toLowerCase()}`">
              <div class="grade-letter">{{ analysisResults.overallGrade }}</div>
              <div class="grade-text">{{ getGradeText(analysisResults.overallGrade) }}</div>
            </div>
            <div class="grade-details">
              <h3>Overall Test Evaluation</h3>
              <p>{{ getGradeDescription(analysisResults.overallGrade) }}</p>
            </div>
          </div>

          <!-- Key metrics -->
          <div class="key-metrics">
            <div class="metric-card reliability">
              <div class="metric-icon">🔒</div>
              <div class="metric-content">
                <h4>Reliability (Cronbach's α)</h4>
                <div class="metric-value" :class="getReliabilityClass(analysisResults.reliability)">
                  {{ (analysisResults.reliability * 100).toFixed(1) }}%
                </div>
                <div class="metric-interpretation">{{ getReliabilityText(analysisResults.reliability) }}</div>
              </div>
            </div>

            <div class="metric-card difficulty">
              <div class="metric-icon">⚖️</div>
              <div class="metric-content">
                <h4>Average Difficulty</h4>
                <div class="metric-value">{{ (analysisResults.summary.averageDifficulty * 100).toFixed(0) }}%</div>
                <div class="metric-interpretation">{{ getDifficultyText(analysisResults.summary.averageDifficulty) }}</div>
              </div>
            </div>

            <div class="metric-card discrimination">
              <div class="metric-icon">🎯</div>
              <div class="metric-content">
                <h4>Average Discrimination</h4>
                <div class="metric-value">{{ (analysisResults.summary.averageDiscrimination * 100).toFixed(0) }}%</div>
                <div class="metric-interpretation">{{ getDiscriminationText(analysisResults.summary.averageDiscrimination) }}</div>
              </div>
            </div>

            <!-- New metrics with real data -->
            <div class="metric-card scores" v-if="analysisResults.descriptiveStats">
              <div class="metric-icon">📊</div>
              <div class="metric-content">
                <h4>Average Score</h4>
                <div class="metric-value">{{ analysisResults.descriptiveStats.meanScore.toFixed(1) }}/{{ analysisResults.testInfo.numItems }}</div>
                <div class="metric-interpretation">{{ getScoreText(analysisResults.descriptiveStats.meanScore, analysisResults.testInfo.numItems) }}</div>
              </div>
            </div>
          </div>

          <!-- Question distribution -->
          <div class="items-distribution">
            <h3>📋 Question Quality Distribution</h3>
            <div class="distribution-grid">
              <div class="distrib-card good">
                <div class="distrib-icon">✅</div>
                <div class="distrib-content">
                  <div class="distrib-number">{{ analysisResults.summary.goodItems }}</div>
                  <div class="distrib-label">Satisfactory Questions</div>
                  <div class="distrib-percentage">{{ Math.round((analysisResults.summary.goodItems / analysisResults.summary.totalItems) * 100) }}%</div>
                </div>
              </div>

              <div class="distrib-card warning">
                <div class="distrib-icon">⚠️</div>
                <div class="distrib-content">
                  <div class="distrib-number">{{ analysisResults.summary.warningItems }}</div>
                  <div class="distrib-label">To Improve</div>
                  <div class="distrib-percentage">{{ Math.round((analysisResults.summary.warningItems / analysisResults.summary.totalItems) * 100) }}%</div>
                </div>
              </div>

              <div class="distrib-card poor">
                <div class="distrib-icon">❌</div>
                <div class="distrib-content">
                  <div class="distrib-number">{{ analysisResults.summary.poorItems }}</div>
                  <div class="distrib-label">Problematic</div>
                  <div class="distrib-percentage">{{ Math.round((analysisResults.summary.poorItems / analysisResults.summary.totalItems) * 100) }}%</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Priority recommendations -->
          <div class="priority-recommendations">
            <h3>🎯 Priority Recommendations</h3>
            <div class="recommendations-list">
              <div 
                v-for="(recommendation, index) in analysisResults.recommendations" 
                :key="index"
                class="recommendation-item"
                :class="getRecommendationClass(recommendation)"
              >
                <div class="recommendation-text">{{ recommendation }}</div>
              </div>
            </div>
          </div>

          <!-- Problematic questions -->
          <div v-if="analysisResults.summary.poorItems > 0" class="problematic-items">
            <h3>🔴 Questions Requiring Immediate Intervention</h3>
            <div class="problematic-list">
              <div 
                v-for="item in analysisResults.itemAnalysis.filter(i => i.status === 'poor')" 
                :key="item.id"
                class="problematic-item"
              >
                <div class="item-header">
                  <span class="item-name">{{ item.name }}</span>
                  <span class="item-metrics">
                    Difficulty: {{ (item.difficulty * 100).toFixed(0) }}% | 
                    Discrimination: {{ (item.discrimination * 100).toFixed(0) }}%
                  </span>
                </div>
                <div class="item-issues">
                  <span v-for="issue in item.issues" :key="issue" class="issue-tag">{{ issue }}</span>
                </div>
                <div class="item-recommendation">💡 {{ item.recommendation }}</div>
              </div>
            </div>
          </div>

          <!-- Report actions -->
          <div class="report-actions">
            <button @click="exportReport" class="btn btn-primary">
              <span class="icon">📄</span>
              Export PDF Report
            </button>
           
            <button @click="resetAnalysis" class="btn btn-link">
              <span class="icon">🔄</span>
              New Analysis
            </button>
          </div>
        </div>

        <!-- Detailed analysis view -->
        <div v-if="activeTab === 'detailed'" class="detailed-section">
          <div class="detailed-header">
            <h3>🔍 Detailed Analysis by Question</h3>
            <p>Individual psychometric analysis of each test item</p>
          </div>

          <!-- Item analysis table -->
          <div class="items-analysis-table">
            <div class="table-header">
              <span class="col-item">Question</span>
              <span class="col-difficulty">Difficulty</span>
              <span class="col-discrimination">Discrimination</span>
              <span class="col-correlation">Correlation</span>
              <span class="col-status">Status</span>
              <span class="col-issues">Identified Issues</span>
              <span class="col-recommendation">Recommendation</span>
            </div>
            
            <div 
              v-for="item in analysisResults.itemAnalysis" 
              :key="item.id"
              class="table-row"
              :class="`status-${item.status}`"
            >
              <span class="col-item">
                <strong>{{ item.name }}</strong>
                <small>{{ item.correctCount }}/{{ item.correctCount + item.incorrectCount }} correct</small>
              </span>
              
              <span class="col-difficulty">
                <div class="metric-display" :class="getDifficultyClass(item.difficulty)">
                  {{ (item.difficulty * 100).toFixed(0) }}%
                </div>
                <small>{{ getDifficultyLabel(item.difficulty) }}</small>
              </span>
              
              <span class="col-discrimination">
                <div class="metric-display" :class="getDiscriminationClass(item.discrimination)">
                  {{ (item.discrimination * 100).toFixed(0) }}%
                </div>
                <small>{{ getDiscriminationLabel(item.discrimination) }}</small>
              </span>
              
              <span class="col-correlation">
                <div class="metric-display" :class="getCorrelationClass(item.itemTotalCorrelation)">
                  {{ (item.itemTotalCorrelation * 100).toFixed(0) }}%
                </div>
                <small>Item-Total</small>
              </span>
              
              <span class="col-status">
                <div class="status-badge" :class="`status-${item.status}`">
                  {{ getStatusIcon(item.status) }} {{ getStatusText(item.status) }}
                </div>
              </span>
              
              <span class="col-issues">
                <div class="issues-list">
                  <span v-for="issue in item.issues" :key="issue" class="issue-chip">
                    {{ issue }}
                  </span>
                </div>
              </span>
              
              <span class="col-recommendation">
                <div class="recommendation-text">
                  💡 {{ item.recommendation }}
                </div>
              </span>
            </div>
          </div>

          <!-- Descriptive statistics -->
          <div v-if="analysisResults.descriptiveStats" class="descriptive-stats">
            <h4>📊 Descriptive Score Statistics</h4>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">Average Score</div>
                <div class="stat-value">{{ analysisResults.descriptiveStats.meanScore.toFixed(2) }}</div>
                <div class="stat-context">/ {{ analysisResults.testInfo.numItems }} points</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Minimum Score</div>
                <div class="stat-value">{{ analysisResults.descriptiveStats.minScore }}</div>
                <div class="stat-context">Lowest score</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Maximum Score</div>
                <div class="stat-value">{{ analysisResults.descriptiveStats.maxScore }}</div>
                <div class="stat-context">Highest score</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Standard Deviation</div>
                <div class="stat-value">{{ analysisResults.descriptiveStats.scoreStandardDeviation.toFixed(2) }}</div>
                <div class="stat-context">Score dispersion</div>
              </div>
            </div>
          </div>

          <!-- Methodological information -->
          <div class="methodology-info">
            <h4>📚 Methodological Information</h4>
            <div class="methodology-grid">
              <div class="method-card">
                <h5>🔒 Reliability (Cronbach's α)</h5>
                <p>Measures the internal consistency of the test. A value ≥ 0.7 is considered acceptable, ≥ 0.8 as good, and ≥ 0.9 as excellent.</p>
              </div>
              <div class="method-card">
                <h5>⚖️ Item Difficulty</h5>
                <p>Proportion of students who succeeded on the item. Ideally between 30% and 70% to maximize discrimination.</p>
              </div>
              <div class="method-card">
                <h5>🎯 Discrimination</h5>
                <p>Item's ability to distinguish strong students from weak ones. Value ≥ 0.3 recommended.</p>
              </div>
              <div class="method-card">
                <h5>📈 Item-Total Correlation</h5>
                <p>Relationship between item score and total score. Value ≥ 0.2 indicates consistency with test objective.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No publications -->
    <div v-else class="no-publications">
      <div class="icon">📊</div>
      <h3>No publications found</h3>
      <p>This test has not been published yet or no data is available.</p>
    </div>

    <!-- Debug info (collapsible) -->
    <details class="debug-section">
      <summary>Debug information</summary>
      <div class="debug-info">
        <p><strong>Debug API Service:</strong></p>
        <p>Test ID: {{ store.testData._id || 'Not defined' }}</p>
        <p>Number of publications: {{ publications.length }}</p>
        <p>API Base URL: {{ baseURL }}</p>
        <p>Raw publications: {{ JSON.stringify(publications, null, 2) }}</p>
      </div>
    </details>
  </div>
</template>

<script setup>
import { useTestStore } from '../../stores/testStore'
import { computed, ref, onMounted } from 'vue'
import { Chart, registerables } from 'chart.js'
import api from "@/services/axios"

// Register all Chart.js components
Chart.register(...registerables)

const store = useTestStore()

defineEmits(['toggle-item-bank'])
defineProps({
  showItemBank: Boolean,
})

// Variables for publications
const publications = ref([])
const loading = ref(false)
const error = ref(null)
const selectedPublications = ref([])
const searchTerm = ref('')
const sortBy = ref('date')
const minParticipants = ref(1)
const analysisMode = ref('grouped')

// Variables for preprocessing
const excludeMultipleAttempts = ref(true)
const excludeIncomplete = ref(true)
const excludeOutliers = ref(false)

// Variables for psychometric analysis
const analysisStarted = ref(false)
const analysisResults = ref(null)
const activeTab = ref('synthesis') // 'synthesis' ou 'detailed'

// For debug
const baseURL = computed(() => import.meta.env.VITE_API_BASE_URL)

// Computed properties for statistics
const totalPublications = computed(() => publications.value.length)
const totalParticipants = computed(() => 
  publications.value.reduce((sum, pub) => sum + (pub.participantCount || 0), 0)
)
const selectedCount = computed(() => selectedPublications.value.length)
const selectedParticipants = computed(() => 
  publications.value
    .filter(pub => selectedPublications.value.includes(pub.id))
    .reduce((sum, pub) => sum + (pub.participantCount || 0), 0)
)

// Selected publications data
const selectedPublicationsData = computed(() => 
  publications.value.filter(pub => selectedPublications.value.includes(pub.id))
)

// Computed properties for data analysis (simulations)
const multipleAttemptsCount = computed(() => {
  return selectedPublicationsData.value.reduce((sum, pub) => {
    // Simulation: 15% of participants have multiple attempts
    return sum + Math.round((pub.participantCount || 0) * 0.15)
  }, 0)
})

const abandonedCount = computed(() => {
  return selectedPublicationsData.value.reduce((sum, pub) => {
    // Simulation: 10% of abandoned sessions
    return sum + Math.round((pub.participantCount || 0) * 0.10)
  }, 0)
})

const hasMultipleAttempts = computed(() => multipleAttemptsCount.value > 0)
const hasIncompleteData = computed(() => abandonedCount.value > 0)
const hasSmallSample = computed(() => selectedParticipants.value < 30)

// Table totals (simulations)
const totalEnrolled = computed(() => 
  selectedPublicationsData.value.reduce((sum, pub) => {
    // Simulation: 120% enrolled compared to participants
    return sum + Math.round((pub.participantCount || 0) * 1.2)
  }, 0)
)

const totalCompleted = computed(() => 
  selectedPublicationsData.value.reduce((sum, pub) => {
    // Simulation: 85% completion
    return sum + Math.round((pub.participantCount || 0) * 0.85)
  }, 0)
)

const totalAbandoned = computed(() => abandonedCount.value)

const overallParticipationRate = computed(() => {
  return totalEnrolled.value > 0 ? (selectedParticipants.value / totalEnrolled.value) * 100 : 0
})

const overallCompletionRate = computed(() => {
  return selectedParticipants.value > 0 ? (totalCompleted.value / selectedParticipants.value) * 100 : 0
})

// Final sample after filtering (simulation)
const finalSampleSize = computed(() => {
  let size = selectedParticipants.value
  
  if (excludeMultipleAttempts.value) {
    size -= multipleAttemptsCount.value
  }
  
  if (excludeIncomplete.value) {
    size -= abandonedCount.value
  }
  
  // Simulation of reduction for outliers
  if (excludeOutliers.value) {
    size = Math.round(size * 0.95) // Simulation of 5% outliers
  }
  
  return Math.max(0, size)
})

// Validation for launch
const canStartAnalysis = computed(() => finalSampleSize.value >= 10)
const hasQualityIssues = computed(() => finalSampleSize.value < 30 || hasMultipleAttempts.value || hasIncompleteData.value)

// Filtered and sorted publications
const filteredPublications = computed(() => {
  let filtered = publications.value

  // Filter by search
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase()
    filtered = filtered.filter(pub => 
      pub.name.toLowerCase().includes(term) ||
      pub.context?.toLowerCase().includes(term) ||
      pub.institution?.toLowerCase().includes(term)
    )
  }

  // Filter by minimum number of participants
  if (minParticipants.value > 1) {
    filtered = filtered.filter(pub => (pub.participantCount || 0) >= minParticipants.value)
  }

  // Sort
  filtered.sort((a, b) => {
    switch (sortBy.value) {
      case 'date':
        return new Date(b.date) - new Date(a.date)
      case 'participants':
        return (b.participantCount || 0) - (a.participantCount || 0)
      case 'completion':
        return (b.completionRate || 0) - (a.completionRate || 0)
      case 'name':
        return a.name.localeCompare(b.name)
      default:
        return 0
    }
  })

  return filtered
})

// Function to load publications
const loadPublications = async () => {
  console.log('=== CHARGEMENT AVEC API SERVICE ===')
  console.log('1. Test ID:', store.testData._id)
  
  if (!store.testData._id) {
    error.value = "No active test detected"
    return
  }

  loading.value = true
  error.value = null
  
  try {
    console.log('2. Appel API: /publications/psychometrics/' + store.testData._id)
    
    const response = await api.get(`/publications/psychometrics/${store.testData._id}`)
    
    console.log('3. Réponse API status:', response.status)
    console.log('4. Réponse API data:', response.data)
    
    publications.value = response.data.publications || []
    console.log('5. Publications chargées:', publications.value.length)
    
  } catch (err) {
    console.error('❌ Erreur API complète:', err)
    
    if (err.response) {
      console.error('Erreur réponse:', err.response.status, err.response.data)
      error.value = `Error ${err.response.status}: ${err.response.data?.error || err.response.data?.message || 'Server error'}`
    } else if (err.request) {
      console.error('Erreur requête:', err.request)
      error.value = "Unable to contact server"
    } else {
      console.error('Erreur générale:', err.message)
      error.value = err.message
    }
  } finally {
    loading.value = false
  }
}

// Selection functions
const isSelected = (id) => selectedPublications.value.includes(id)

const toggleSelection = (id) => {
  const index = selectedPublications.value.indexOf(id)
  if (index > -1) {
    selectedPublications.value.splice(index, 1)
  } else {
    selectedPublications.value.push(id)
  }
}

const selectAll = () => {
  selectedPublications.value = filteredPublications.value.map(pub => pub.id)
}

const selectNone = () => {
  selectedPublications.value = []
}

const selectRecent = () => {
  const recent = [...publications.value]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3)
    .map(pub => pub.id)
  selectedPublications.value = recent
}

const selectLargest = () => {
  const largest = [...publications.value]
    .sort((a, b) => (b.participantCount || 0) - (a.participantCount || 0))
    .slice(0, 3)
    .map(pub => pub.id)
  selectedPublications.value = largest
}

// Utility functions
const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getCompletionClass = (rate) => {
  if (rate >= 90) return 'excellent'
  if (rate >= 75) return 'good'
  if (rate >= 60) return 'average'
  return 'poor'
}

const getPowerClass = (participants) => {
  if (participants >= 100) return 'excellent'
  if (participants >= 50) return 'good'
  if (participants >= 30) return 'average'
  return 'poor'
}

const getPowerText = (participants) => {
  if (participants >= 100) return 'Excellente'
  if (participants >= 50) return 'Bonne'
  if (participants >= 30) return 'Acceptable'
  return 'Limitée'
}

const getAvailableAnalyses = (participants) => {
  const analyses = []
  if (participants >= 30) analyses.push('Fiabilité')
  if (participants >= 50) analyses.push('Validité')
  if (participants >= 100) analyses.push('TRI')
  return analyses.length > 0 ? analyses.join(', ') : 'Analyses descriptives'
}

const getRecommendedAnalyses = (participants) => {
  const analyses = []
  if (participants >= 100) {
    analyses.push('Théorie de Réponse à l\'Item (TRI)')
    analyses.push('Analyse factorielle')
    analyses.push('Fiabilité (α de Cronbach)')
  } else if (participants >= 50) {
    analyses.push('Analyse factorielle exploratoire')
    analyses.push('Fiabilité (α de Cronbach)')
    analyses.push('Corrélations item-total')
  } else if (participants >= 30) {
    analyses.push('Fiabilité (α de Cronbach)')
    analyses.push('Statistiques descriptives')
  } else {
    analyses.push('Statistiques descriptives uniquement')
  }
  return analyses.join(', ')
}

// Nouvelles fonctions utilitaires
const formatRate = (rate) => {
  return isNaN(rate) ? 0 : Math.round(rate)
}

const getRateClass = (rate) => {
  if (rate >= 80) return 'excellent'
  if (rate >= 60) return 'good'
  if (rate >= 40) return 'average'
  return 'poor'
}

const getSampleQualityClass = (size) => {
  if (size >= 100) return 'excellent'
  if (size >= 50) return 'good'  
  if (size >= 30) return 'average'
  return 'poor'
}

const getQualityWarnings = () => {
  const warnings = []
  
  if (finalSampleSize.value < 30) {
    warnings.push('Sample too small for robust psychometric analyses')
  }
  
  if (hasMultipleAttempts.value && !excludeMultipleAttempts.value) {
    warnings.push('Multiple attempts may bias results (learning effect)')
  }
  
  if (hasIncompleteData.value && !excludeIncomplete.value) {
    warnings.push('Incomplete data may introduce selection bias')
  }
  
  if (overallCompletionRate.value < 70) {
    warnings.push('Low completion rate, check instrument quality')
  }
  
  return warnings
}

// Actions
const previewPublication = (publication) => {
  console.log('Preview of:', publication.name)
  alert(`Preview of "${publication.name}"\n\nParticipants: ${publication.participantCount}\nCompletion: ${publication.completionRate}%\nDuration: ${publication.duration}`)
}

const startAnalysis = () => {
  console.log('Start psychometric analysis:', {
    testId: store.testData._id,
    publications: selectedPublications.value,
    mode: analysisMode.value,
    participants: selectedParticipants.value
  })
  
  alert(`🚀 Psychometric analysis configured!\n\nMode: ${analysisMode.value}\nPublications: ${selectedCount.value}\nParticipants: ${selectedParticipants.value}\n\nNext step: Implementation of statistical analyses`)
}

const exportSelection = () => {
  const selection = {
    testId: store.testData._id,
    analysisMode: analysisMode.value,
    publications: selectedPublications.value,
    publicationsData: publications.value.filter(pub => selectedPublications.value.includes(pub.id))
  }
  
  const blob = new Blob([JSON.stringify(selection, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `psychometrics-selection-${store.testData._id}.json`
  a.click()
  URL.revokeObjectURL(url)
}

// New action functions
const validateAndStartAnalysis = async () => {
  const warnings = getQualityWarnings()
  
  if (warnings.length > 0) {
    const message = `⚠️ Warnings detected:\n\n${warnings.map(w => `• ${w}`).join('\n')}\n\nDo you want to continue despite these warnings?`
    
    if (!confirm(message)) {
      return
    }
  }
  
  // Launch REAL psychometric analysis
  await startRealPsychometricAnalysis()
}

const startRealPsychometricAnalysis = async () => {
  if (selectedPublications.value.length === 0) {
    alert('Please select at least one publication for analysis.')
    return
  }
  
  // For demo, analyze the first selected publication
  const selectedPubId = selectedPublications.value[0]
  const selectedPub = publications.value.find(p => p.id === selectedPubId)
  
  if (!selectedPub) {
    alert('Selected publication not found.')
    return
  }
  
  console.log(`🔬 Launching psychometric analysis for: ${selectedPub.name}`)
  console.log(`📋 Test ID: ${store.testData._id}`)
  console.log(`📊 Publication ID: ${selectedPubId}`)
  
  // Show loading indicator
  analysisStarted.value = true
  analysisResults.value = {
    loading: true,
    publicationName: selectedPub.name,
    sampleSize: selectedPub.participantCount
  }
  
  try {
    // Call real psychometric analysis API
    const response = await api.get(`/publications/psychometrics/${store.testData._id}/analyze/${selectedPubId}`)
    
    console.log('✅ Psychometric analysis completed:', response.data)
    
    // Store real results
    analysisResults.value = response.data
    
  } catch (error) {
    console.error('❌ Error during psychometric analysis:', error)
    
    // In case of error, show message and fallback to simulated data
    const errorMessage = error.response?.data?.error || error.message || 'Erreur inconnue'
    
    if (confirm(`❌ Error during real analysis: ${errorMessage}\n\nDo you want to use simulated data for demonstration?`)) {
      // Fallback to simulation
      generateAnalysisResults()
    } else {
      // Cancel analysis
      analysisStarted.value = false
      analysisResults.value = null
    }
  }
}

const generateAnalysisResults = () => {
  // Realistic simulation based on preprocessing data
  const sampleSize = finalSampleSize.value
  const numItems = 15 // Simulation of a test with 15 questions
  
  // Global reliability calculation (simulated Cronbach's α)
  const reliability = Math.max(0.65, Math.min(0.95, 0.75 + (sampleSize - 30) * 0.003 + Math.random() * 0.1))
  
  // Generate data per question
  const itemAnalysis = []
  for (let i = 1; i <= numItems; i++) {
    const difficulty = 0.3 + Math.random() * 0.4 // Between 30% and 70% success
    const discrimination = 0.2 + Math.random() * 0.6 // Between 0.2 and 0.8
    const itemTotal = 0.3 + Math.random() * 0.5 // Item-total correlation
    
    // Determine question status
    let status = 'good'
    let issues = []
    
    if (difficulty < 0.2) {
      status = 'poor'
      issues.push('Too difficult')
    } else if (difficulty > 0.9) {
      status = 'poor' 
      issues.push('Too easy')
    } else if (difficulty < 0.3 || difficulty > 0.8) {
      status = 'warning'
      issues.push('Extreme difficulty')
    }
    
    if (discrimination < 0.3) {
      status = 'poor'
      issues.push('Low discrimination')
    } else if (discrimination < 0.4) {
      status = status === 'good' ? 'warning' : status
      issues.push('Limited discrimination')
    }
    
    if (itemTotal < 0.2) {
      status = 'poor'
      issues.push('Low correlation')
    }
    
    if (issues.length === 0) {
      issues.push('No problem detected')
    }
    
    itemAnalysis.push({
      id: i,
      name: `Question ${i}`,
      difficulty: difficulty,
      discrimination: discrimination,
      itemTotalCorrelation: itemTotal,
      averageTime: 45 + Math.random() * 60, // Time in seconds
      status: status,
      issues: issues,
      recommendation: getItemRecommendation(status, issues)
    })
  }
  
  // Calculate global metrics
  const poorItems = itemAnalysis.filter(item => item.status === 'poor').length
  const warningItems = itemAnalysis.filter(item => item.status === 'warning').length
  const goodItems = itemAnalysis.filter(item => item.status === 'good').length
  
  // Overall test score
  let overallGrade = 'A'
  if (reliability < 0.7 || poorItems > 3) overallGrade = 'D'
  else if (reliability < 0.8 || poorItems > 1 || warningItems > 5) overallGrade = 'C'
  else if (reliability < 0.85 || warningItems > 3) overallGrade = 'B'
  
  analysisResults.value = {
    timestamp: new Date().toISOString(),
    sampleSize: sampleSize,
    reliability: reliability,
    overallGrade: overallGrade,
    itemAnalysis: itemAnalysis,
    summary: {
      totalItems: numItems,
      goodItems: goodItems,
      warningItems: warningItems,
      poorItems: poorItems,
      averageDifficulty: itemAnalysis.reduce((sum, item) => sum + item.difficulty, 0) / numItems,
      averageDiscrimination: itemAnalysis.reduce((sum, item) => sum + item.discrimination, 0) / numItems
    },
    recommendations: generateGlobalRecommendations(overallGrade, poorItems, warningItems, reliability)
  }
}

const getItemRecommendation = (status, issues) => {
  if (status === 'poor') {
    if (issues.includes('Too difficult')) return 'Simplify wording or add hints'
    if (issues.includes('Too easy')) return 'Make more complex or add distractors'
    if (issues.includes('Low discrimination')) return 'Review response option construction'
    if (issues.includes('Low correlation')) return 'Check consistency with test objectives'
  }
  if (status === 'warning') {
    return 'Slightly revise wording'
  }
  return 'Question of satisfactory quality'
}

const generateGlobalRecommendations = (grade, poorItems, warningItems, reliability) => {
  const recommendations = []
  
  if (grade === 'D') {
    recommendations.push('🔴 HIGH PRIORITY: Major test revision needed')
  } else if (grade === 'C') {
    recommendations.push('🟠 Significant improvement recommended')
  }
  
  if (reliability < 0.7) {
    recommendations.push('Improve reliability (α < 0.7) by revising problematic questions')
  }
  
  if (poorItems > 0) {
    recommendations.push(`Remove or rewrite the ${poorItems} failing question(s)`)
  }
  
  if (warningItems > 3) {
    recommendations.push(`Review the ${warningItems} flagged questions`)
  }
  
  if (recommendations.length === 0) {
    recommendations.push('✅ Test of good psychometric quality')
  }
  
  return recommendations
}

const exportPreprocessedData = () => {
  const preprocessedData = {
    testId: store.testData._id,
    analysisMode: analysisMode.value,
    publications: selectedPublications.value,
    preprocessing: {
      excludeMultipleAttempts: excludeMultipleAttempts.value,
      excludeIncomplete: excludeIncomplete.value,
      excludeOutliers: excludeOutliers.value
    },
    statistics: {
      originalSample: selectedParticipants.value,
      finalSample: finalSampleSize.value,
      multipleAttempts: multipleAttemptsCount.value,
      abandoned: abandonedCount.value,
      participationRate: overallParticipationRate.value,
      completionRate: overallCompletionRate.value
    },
    qualityAssessment: {
      warnings: getQualityWarnings(),
      recommendedAnalyses: getRecommendedAnalyses(finalSampleSize.value),
      powerAssessment: getPowerText(finalSampleSize.value)
    },
    publicationsData: selectedPublicationsData.value
  }
  
  const blob = new Blob([JSON.stringify(preprocessedData, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `psychometrics-preprocessed-${store.testData._id}-${new Date().toISOString().split('T')[0]}.json`
  a.click()
  URL.revokeObjectURL(url)
  
  alert('✅ Preprocessed data exported successfully!')
}

const showMethodologyGuide = () => {
  const guide = `
📚 METHODOLOGICAL GUIDE - PSYCHOMETRIC ANALYSES

🎯 OBJECTIVES
- Evaluate the psychometric quality of your instrument
- Analyze the reliability and validity of measurements
- Detect problematic items

⚠️ METHODOLOGICAL PREREQUISITES

1. SAMPLE SIZE
   • N ≥ 100: IRT analyses, robust factorial
   • N ≥ 50: Exploratory factor analyses
   • N ≥ 30: Reliable reliability (Cronbach's α)
   • N < 30: Descriptive statistics only

2. DATA QUALITY
   • Avoid multiple attempts (learning effect)
   • Exclude incomplete responses (selection bias)
   • Detect aberrant response patterns

3. TYPES OF ANALYSES
   • Reliability: Internal consistency (Cronbach's α ≥ 0.70)
   • Validity: Factorial structure, correlations
   • IRT: Rasch models, 2PL, 3PL
   • Differential analysis: Item bias (DIF)

📊 INTERPRETATION
- α ≥ 0.90: Excellent reliability
- α ≥ 0.80: Good reliability  
- α ≥ 0.70: Acceptable
- α < 0.70: Questionable

🔧 RECOMMENDED ACTIONS
1. Examine item-total correlations
2. Identify items with low discrimination
3. Analyze dimensional structure
4. Check for absence of item bias
  `
  
  alert(guide)
}

// Utility functions pour le rapport
const formatDateTime = (isoString) => {
  return new Date(isoString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getGradeText = (grade) => {
  const texts = {
    'A': 'Excellent',
    'B': 'Good',
    'C': 'Average',
    'D': 'Insufficient'
  }
  return texts[grade] || 'Not evaluated'
}

const getGradeDescription = (grade) => {
  const descriptions = {
    'A': 'Your test shows excellent psychometric qualities. Reliability is high and questions are well constructed.',
    'B': 'Your test shows good psychometric qualities with some possible improvements.',
    'C': 'Your test requires significant improvements to reach a satisfactory quality level.',
    'D': 'Your test has major flaws that compromise its validity. Significant revision is needed.'
  }
  return descriptions[grade] || 'Evaluation in progress...'
}

const getReliabilityClass = (reliability) => {
  if (reliability >= 0.9) return 'excellent'
  if (reliability >= 0.8) return 'good'
  if (reliability >= 0.7) return 'average'
  return 'poor'
}

const getReliabilityText = (reliability) => {
  if (reliability >= 0.9) return 'Excellent reliability'
  if (reliability >= 0.8) return 'Good reliability'
  if (reliability >= 0.7) return 'Acceptable reliability'
  return 'Insufficient reliability'
}

const getDifficultyText = (difficulty) => {
  if (difficulty < 0.3) return 'Very difficult test'
  if (difficulty < 0.5) return 'Difficult test'
  if (difficulty < 0.7) return 'Moderately difficult test'
  if (difficulty < 0.9) return 'Easy test'
  return 'Very easy test'
}

const getDiscriminationText = (discrimination) => {
  if (discrimination >= 0.7) return 'Excellent discriminative power'
  if (discrimination >= 0.5) return 'Good discriminative power'
  if (discrimination >= 0.3) return 'Acceptable discriminative power'
  return 'Low discriminative power'
}

const getScoreText = (meanScore, totalItems) => {
  const percentage = (meanScore / totalItems) * 100
  if (percentage >= 80) return 'Very good level'
  if (percentage >= 65) return 'Good level'
  if (percentage >= 50) return 'Average level'
  if (percentage >= 35) return 'Low level'
  return 'Very low level'
}

const getRecommendationClass = (recommendation) => {
  if (recommendation.includes('🔴')) return 'critical'
  if (recommendation.includes('🟠')) return 'warning'
  if (recommendation.includes('✅')) return 'success'
  return 'info'
}

// Report actions
const exportReport = () => {
  const reportData = {
    title: 'Psychometric Analysis Report',
    timestamp: analysisResults.value.timestamp,
    sampleSize: analysisResults.value.sampleSize,
    overallGrade: analysisResults.value.overallGrade,
    reliability: analysisResults.value.reliability,
    summary: analysisResults.value.summary,
    recommendations: analysisResults.value.recommendations,
    itemAnalysis: analysisResults.value.itemAnalysis
  }
  
  const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `rapport-psychometrique-${store.testData._id}-${new Date().toISOString().split('T')[0]}.json`
  a.click()
  URL.revokeObjectURL(url)
  
  alert('📄 Report exported successfully!\n\nNote: In a complete version, this would generate a professional PDF.')
}

const viewDetailedAnalysis = () => {
  alert('🔍 Detailed analysis by question\n\nThis feature would display a detailed view of each question with charts and in-depth metrics.')
}

const resetAnalysis = () => {
  if (confirm('Do you really want to restart the analysis? Current results will be lost.')) {
    analysisStarted.value = false
    analysisResults.value = null
  }
}

// Helper functions for detailed analysis view
const getDifficultyClass = (difficulty) => {
  if (difficulty < 0.2) return 'very-difficult'
  if (difficulty < 0.3) return 'difficult'
  if (difficulty > 0.9) return 'very-easy'
  if (difficulty > 0.7) return 'easy'
  return 'moderate'
}

const getDifficultyLabel = (difficulty) => {
  if (difficulty < 0.2) return 'Very difficult'
  if (difficulty < 0.3) return 'Difficult'
  if (difficulty > 0.9) return 'Very easy'
  if (difficulty > 0.7) return 'Easy'
  return 'Moderate'
}

const getDiscriminationClass = (discrimination) => {
  if (discrimination < 0.2) return 'very-poor'
  if (discrimination < 0.3) return 'poor'
  if (discrimination < 0.4) return 'acceptable'
  return 'good'
}

const getDiscriminationLabel = (discrimination) => {
  if (discrimination < 0.2) return 'Very low'
  if (discrimination < 0.3) return 'Low'
  if (discrimination < 0.4) return 'Acceptable'
  return 'Good'
}

const getCorrelationClass = (correlation) => {
  if (correlation < 0.2) return 'very-poor'
  if (correlation < 0.3) return 'poor'
  if (correlation < 0.4) return 'acceptable'
  return 'good'
}

const getStatusIcon = (status) => {
  const icons = {
    'good': '✅',
    'warning': '⚠️',
    'poor': '❌'
  }
  return icons[status] || '❓'
}

const getStatusText = (status) => {
  const texts = {
    'good': 'Satisfactory',
    'warning': 'To improve',
    'poor': 'Problematic'
  }
  return texts[status] || 'Unknown'
}

// Lifecycle
onMounted(() => {
  console.log('HELOOOO - Component mounted avec API service')
  loadPublications()
})

console.log('HELOOOO')
</script>

<style scoped>
.psychometrics-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h2 {
  color: #2c3e50;
  margin-bottom: 8px;
}

.subtitle {
  color: #6c757d;
  font-size: 1.1em;
}

.subtitle-note {
  color: #dc3545;
  font-size: 0.9em;
  font-style: italic;
  margin-top: 5px;
}

.global-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.3s ease;
}

.summary-card:hover {
  transform: translateY(-2px);
}

.summary-card .icon {
  font-size: 2em;
}

.summary-card.total .icon { color: #007bff; }
.summary-card.participants .icon { color: #28a745; }
.summary-card.selected .icon { color: #ffc107; }
.summary-card.sample .icon { color: #dc3545; }

.summary-card .content h3 {
  font-size: 2em;
  margin: 0;
  color: #2c3e50;
}

.summary-card .content span {
  color: #6c757d;
  font-size: 0.9em;
}

.loading-section {
  text-align: center;
  padding: 60px 20px;
  background: #f8f9fa;
  border-radius: 12px;
  margin: 20px 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.analysis-loading {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 40px;
  margin-top: 40px;
  color: white;
  text-align: center;
}

.loading-container {
  max-width: 500px;
  margin: 0 auto;
}

.analysis-loading .loading-spinner {
  width: 60px;
  height: 60px;
  border: 6px solid rgba(255, 255, 255, 0.3);
  border-top: 6px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 30px;
}

.analysis-loading h3 {
  margin: 0 0 20px 0;
  font-size: 1.8em;
}

.analysis-loading p {
  margin: 10px 0;
  opacity: 0.9;
  font-size: 1.1em;
}

.error-section {
  text-align: center;
  padding: 40px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  color: #721c24;
  margin: 20px 0;
}

.no-publications {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.no-publications .icon {
  font-size: 4em;
  margin-bottom: 20px;
}

.quick-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.filters-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-weight: 600;
  color: #495057;
  font-size: 0.9em;
}

.search-input, .sort-select, .number-input {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9em;
}

.publications-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
}

.publication-card {
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.publication-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.publication-card.selected {
  border-color: #007bff;
  background: #f8f9ff;
}

.publication-card.recommended {
  border-left: 4px solid #28a745;
}

.publication-card.warning {
  border-left: 4px solid #ffc107;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 15px;
  border-bottom: 1px solid #eee;
}

.selection-area {
  position: relative;
}

.publication-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.publication-info {
  flex: 1;
}

.publication-info h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 1.2em;
}

.meta-info {
  display: flex;
  gap: 15px;
  font-size: 0.9em;
  color: #6c757d;
  flex-wrap: wrap;
}

.completion-badge {
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8em;
  font-weight: bold;
}

.completion-badge.excellent {
  background: #d4edda;
  color: #155724;
}

.completion-badge.good {
  background: #d1ecf1;
  color: #0c5460;
}

.completion-badge.average {
  background: #fff3cd;
  color: #856404;
}

.completion-badge.poor {
  background: #f8d7da;
  color: #721c24;
}

.card-body {
  padding: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-item .label {
  color: #6c757d;
  font-size: 0.9em;
}

.stat-item .value {
  font-weight: 600;
  color: #2c3e50;
}

.participants-count {
  color: #007bff;
  font-size: 1.1em;
}

.quality-indicators {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.8em;
}

.indicator.good {
  background: #d4edda;
  color: #155724;
}

.indicator.warning {
  background: #fff3cd;
  color: #856404;
}

.card-footer {
  padding: 15px 20px;
  background: #f8f9fa;
  display: flex;
  gap: 15px;
  border-top: 1px solid #eee;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-link {
  background: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.analysis-config {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  margin-top: 30px;
}

.config-header h3 {
  margin: 0 0 5px 0;
}

.config-header p {
  margin: 0;
  opacity: 0.9;
}

.config-options {
  margin: 25px 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.option-group label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  font-weight: 500;
}

.option-title {
  font-weight: 600;
}

.option-group small {
  display: block;
  margin-left: 25px;
  opacity: 0.8;
  font-size: 0.85em;
}

.analysis-preview {
  background: rgba(255,255,255,0.1);
  padding: 20px;
  border-radius: 8px;
  margin: 20px 0;
}

.analysis-preview h4 {
  margin: 0 0 15px 0;
}

.preview-stats {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.preview-item .label {
  opacity: 0.9;
}

.preview-item .value {
  font-weight: 600;
}

.preview-item .value.excellent {
  color: #90EE90;
}

.preview-item .value.good {
  color: #FFE4B5;
}

.preview-item .value.average {
  color: #FFA500;
}

.preview-item .value.poor {
  color: #FFB6C1;
}

.analysis-actions {
  display: flex;
  gap: 15px;
  margin-top: 25px;
  flex-wrap: wrap;
}

.analysis-actions .btn {
  padding: 12px 24px;
  font-size: 1em;
}

.debug-section {
  margin-top: 40px;
  background: #f8f9fa;
  border-radius: 8px;
}

.debug-section summary {
  padding: 15px;
  cursor: pointer;
  font-weight: 600;
  color: #6c757d;
}

.debug-info {
  padding: 15px;
  border-top: 1px solid #dee2e6;
  font-family: monospace;
  font-size: 0.8em;
  background: #fff;
  border-radius: 0 0 8px 8px;
  max-height: 300px;
  overflow-y: auto;
}

.data-preprocessing {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 30px;
  margin-top: 30px;
  border: 2px solid #dee2e6;
}

.preprocessing-header {
  text-align: center;
  margin-bottom: 30px;
}

.preprocessing-header h3 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.warning-text {
  color: #856404;
  background: #fff3cd;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ffeaa7;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
}

.methodology-section {
  margin-bottom: 30px;
}

.methodology-section h4 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.issues-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.issue-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.issue-card.active {
  border-color: #ffc107;
  background: #fff9e6;
}

.issue-card.multiple-attempts.active {
  border-color: #dc3545;
  background: #f8d7da;
}

.issue-card.incomplete-data.active {
  border-color: #fd7e14;
  background: #fef1e8;
}

.issue-card.sample-size.active {
  border-color: #6f42c1;
  background: #f3f0ff;
}

.issue-icon {
  font-size: 2em;
  min-width: 50px;
  text-align: center;
}

.issue-content h5 {
  margin: 0 0 8px 0;
  color: #2c3e50;
}

.issue-content p {
  margin: 0 0 5px 0;
  font-weight: 600;
  color: #495057;
}

.issue-content small {
  color: #6c757d;
  font-style: italic;
  line-height: 1.4;
}

.detailed-stats {
  margin-bottom: 30px;
}

.detailed-stats h4 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.stats-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 15px;
  padding: 15px;
  background: #495057;
  color: white;
  font-weight: 600;
  font-size: 0.9em;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 15px;
  padding: 12px 15px;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
}

.table-row:nth-child(even) {
  background: #f8f9fa;
}

.table-row.total-row {
  background: #e9ecef;
  font-weight: 600;
  border-top: 2px solid #495057;
}

.pub-name {
  font-weight: 500;
  color: #2c3e50;
}

.participation-rate, .completion-rate {
  font-weight: 600;
}

.filtering-options {
  margin-bottom: 30px;
}

.filtering-options h4 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.filter-controls {
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.filter-controls .filter-group {
  margin-bottom: 15px;
}

.filter-controls .filter-group:last-child {
  margin-bottom: 0;
}

.filter-controls label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}

.filter-controls .option-title {
  font-weight: 600;
  color: #2c3e50;
}

.filter-controls small {
  display: block;
  margin-left: 25px;
  color: #6c757d;
  font-style: italic;
  margin-top: 5px;
}

.final-sample {
  margin-bottom: 30px;
}

.final-sample h4 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.sample-summary {
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 15px;
}

.sample-stat {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.sample-stat:last-child {
  margin-bottom: 0;
}

.sample-stat .label {
  color: #6c757d;
  font-weight: 500;
}

.sample-stat .value {
  font-weight: 600;
}

.quality-warnings {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 15px;
}

.quality-warnings h5 {
  color: #856404;
  margin: 0 0 10px 0;
}

.quality-warnings ul {
  margin: 0;
  color: #856404;
}

.quality-warnings li {
  margin-bottom: 5px;
}

.btn-warning {
  background: #ffc107 !important;
  color: #212529 !important;
}

.btn-warning:hover {
  background: #e0a800 !important;
}

/* Styles for psychometric report */
.psychometric-report {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 40px;
  margin-top: 40px;
  color: white;
}

.report-header {
  text-align: center;
  margin-bottom: 40px;
}

.report-header h2 {
  margin: 0 0 15px 0;
  font-size: 2.5em;
  font-weight: 700;
}

.report-meta {
  display: flex;
  justify-content: center;
  gap: 30px;
  opacity: 0.9;
  font-size: 1.1em;
}

.synthesis-section {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Overall grade */
.overall-grade-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 16px;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 30px;
  border: 1px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.grade-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
}

.grade-letter {
  font-size: 4em;
  font-weight: 900;
  line-height: 1;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.grade-text {
  font-size: 1.2em;
  font-weight: 600;
  margin-top: 5px;
  color: #495057;
}

.grade-a { color: #4CAF50; }
.grade-b { color: #8BC34A; }
.grade-c { color: #FF9800; }
.grade-d { color: #F44336; }

.grade-details h3 {
  margin: 0 0 10px 0;
  font-size: 1.8em;
  color: #2c3e50;
  font-weight: 700;
}

.grade-details p {
  margin: 0;
  font-size: 1.1em;
  line-height: 1.5;
  color: #495057;
}

/* Key metrics */
.key-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.metric-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.metric-icon {
  font-size: 3em;
  min-width: 60px;
  text-align: center;
}

.metric-content h4 {
  margin: 0 0 8px 0;
  font-size: 1.1em;
  color: #495057;
  font-weight: 600;
}

.metric-value {
  font-size: 2.2em;
  font-weight: 700;
  margin-bottom: 5px;
  color: #2c3e50;
}

.metric-interpretation {
  font-size: 0.9em;
  color: #6c757d;
}

/* Question distribution */
.items-distribution {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.items-distribution h3 {
  margin: 0 0 25px 0;
  text-align: center;
  font-size: 1.6em;
  color: #2c3e50;
}

.distribution-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.distrib-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.distrib-card:hover {
  transform: translateY(-5px);
  border-color: #ced4da;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.distrib-card.good { border-color: #28a745; background: #f8fff8; }
.distrib-card.warning { border-color: #ffc107; background: #fffef8; }
.distrib-card.poor { border-color: #dc3545; background: #fff8f8; }

.distrib-icon {
  font-size: 2.5em;
  margin-bottom: 10px;
}

.distrib-number {
  font-size: 3em;
  font-weight: 900;
  line-height: 1;
  color: #2c3e50;
}

.distrib-label {
  font-size: 1.1em;
  margin: 8px 0;
  color: #495057;
  font-weight: 600;
}

.distrib-percentage {
  font-size: 1.3em;
  font-weight: 600;
  color: #6c757d;
}

/* Recommendations */
.priority-recommendations {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.priority-recommendations h3 {
  margin: 0 0 20px 0;
  font-size: 1.6em;
  color: #2c3e50;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.recommendation-item {
  padding: 15px 20px;
  border-radius: 8px;
  border-left: 4px solid;
}

.recommendation-item.critical {
  background: rgba(244, 67, 54, 0.2);
  border-color: #F44336;
}

.recommendation-item.warning {
  background: rgba(255, 152, 0, 0.2);
  border-color: #FF9800;
}

.recommendation-item.success {
  background: rgba(76, 175, 80, 0.2);
  border-color: #4CAF50;
}

.recommendation-item.info {
  background: rgba(33, 150, 243, 0.2);
  border-color: #2196F3;
}

.recommendation-text {
  font-size: 1.1em;
  line-height: 1.4;
  color: #2c3e50;
  font-weight: 500;
}

/* Problematic questions */
.problematic-items {
  background: #fff5f5;
  border: 2px solid #dc3545;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.problematic-items h3 {
  margin: 0 0 25px 0;
  color: #dc3545;
  font-size: 1.6em;
  font-weight: 700;
}

.problematic-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.problematic-item {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #dc3545;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.item-name {
  font-weight: 600;
  font-size: 1.2em;
  color: #2c3e50;
}

.item-metrics {
  font-size: 0.9em;
  color: #6c757d;
}

.item-issues {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.issue-tag {
  background: #dc3545;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 500;
}

.item-recommendation {
  font-style: italic;
  color: #495057;
  margin-top: 10px;
  font-weight: 500;
}

/* Report actions */
.report-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 30px;
  flex-wrap: wrap;
}

.report-actions .btn {
  padding: 15px 25px;
  font-size: 1.1em;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
}

.report-actions .btn.btn-primary {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.report-actions .btn.btn-secondary {
  background: #6c757d;
  color: white;
  border-color: #6c757d;
}

.report-actions .btn.btn-link {
  background: #f8f9fa;
  color: #007bff;
  border-color: #007bff;
}

.report-actions .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.report-actions .btn.btn-primary:hover {
  background: #0056b3;
  border-color: #0056b3;
}

.report-actions .btn.btn-secondary:hover {
  background: #545b62;
  border-color: #545b62;
}

.report-actions .btn.btn-link:hover {
  background: #007bff;
  color: white;
}

@media (max-width: 768px) {
  .global-summary {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .meta-info {
    flex-direction: column;
    gap: 8px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .quality-indicators {
    flex-direction: column;
    gap: 8px;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 10px;
  }
  
  .analysis-actions {
    flex-direction: column;
  }
  
  .quick-actions {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .issues-grid {
    grid-template-columns: 1fr;
  }
  
  .table-header, .table-row {
    grid-template-columns: 1fr;
    gap: 5px;
  }
  
  .table-header span, .table-row span {
    padding: 5px 0;
  }
  
  .table-header {
    display: none;
  }
  
  .table-row span:before {
    content: attr(data-label) ': ';
    font-weight: 600;
    color: #495057;
  }
  
  /* Responsive psychometric report */
  .psychometric-report {
    padding: 20px;
  }
  
  .report-header h2 {
    font-size: 2em;
  }
  
  .report-meta {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .overall-grade-card {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
  
  .key-metrics {
    grid-template-columns: 1fr;
  }
  
  .metric-card {
    flex-direction: column;
    text-align: center;
  }
  
  .distribution-grid {
    grid-template-columns: 1fr;
  }
  
  .report-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}

/* Tab system styles */
.report-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 0;
  border-bottom: 3px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 12px 12px 0 0;
  padding: 8px 8px 0 8px;
}

.tab-button {
  padding: 15px 25px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  font-size: 0.95em;
  border: 2px solid transparent;
  border-bottom: none;
  margin-bottom: -3px;
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.7);
  color: #495057;
  transform: translateY(-2px);
}

.tab-button.active {
  background: white;
  color: #007bff;
  font-weight: 600;
  border: 2px solid #e9ecef;
  border-bottom: 3px solid white;
  box-shadow: 0 -2px 8px rgba(0, 123, 255, 0.1);
  z-index: 1;
}

.tab-button.active::before {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  right: 0;
  height: 3px;
  background: white;
}

/* Tab content container */
.synthesis-section,
.detailed-section {
  background: white;
  border: 2px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 12px 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.detailed-header {
  text-align: center;
  margin-bottom: 40px;
}

.detailed-header h3 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.detailed-header p {
  color: #6c757d;
  font-size: 1.1em;
}

.items-analysis-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 40px;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr 2fr 2.5fr;
  gap: 16px;
  padding: 20px;
  background: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr 2fr 2.5fr;
  gap: 16px;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background: #f8f9fa;
}

.table-row.status-good {
  border-left: 4px solid #28a745;
}

.table-row.status-warning {
  border-left: 4px solid #ffc107;
}

.table-row.status-poor {
  border-left: 4px solid #dc3545;
}

.col-item strong {
  display: block;
  color: #2c3e50;
  font-size: 1.05em;
  margin-bottom: 4px;
}

.col-item small {
  color: #6c757d;
  font-size: 0.85em;
}

.metric-display {
  font-weight: 600;
  font-size: 1.1em;
  padding: 4px 8px;
  border-radius: 4px;
  text-align: center;
  margin-bottom: 4px;
}

.metric-display.very-difficult { background: #ffeaa7; color: #d63031; }
.metric-display.difficult { background: #fdcb6e; color: #e17055; }
.metric-display.easy { background: #a7e2a7; color: #00b894; }
.metric-display.very-easy { background: #55a3ff; color: #0984e3; }
.metric-display.moderate { background: #ddd6fe; color: #6c5ce7; }

.metric-display.very-poor { background: #ff7675; color: #d63031; }
.metric-display.poor { background: #fdcb6e; color: #e17055; }
.metric-display.acceptable { background: #fd79a8; color: #e84393; }
.metric-display.good { background: #00b894; color: white; }

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
}

.status-badge.status-good {
  background: #d4edda;
  color: #155724;
}

.status-badge.status-warning {
  background: #fff3cd;
  color: #856404;
}

.status-badge.status-poor {
  background: #f8d7da;
  color: #721c24;
}

.issues-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.issue-chip {
  background: #e9ecef;
  color: #495057;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  text-align: center;
}

.recommendation-text {
  background: #e3f2fd;
  color: #1565c0;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.9em;
  line-height: 1.4;
}

/* Descriptive stats */
.descriptive-stats {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 30px;
  margin-bottom: 40px;
}

.descriptive-stats h4 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
}

.stat-label {
  font-size: 0.9em;
  color: #6c757d;
  margin-bottom: 8px;
  font-weight: 500;
}

.stat-value {
  font-size: 2em;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
}

.stat-context {
  font-size: 0.8em;
  color: #868e96;
}

/* Methodology info */
.methodology-info {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 30px;
}

.methodology-info h4 {
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.methodology-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.method-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #007bff;
}

.method-card h5 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 1.1em;
}

.method-card p {
  color: #6c757d;
  line-height: 1.5;
  margin: 0;
  font-size: 0.95em;
}

/* Responsive for detailed view */
@media (max-width: 1200px) {
  .table-header,
  .table-row {
    grid-template-columns: 1.5fr 1fr 1fr 1fr 1.2fr 1.8fr 2fr;
    gap: 12px;
    font-size: 0.9em;
  }
}

@media (max-width: 768px) {
  .items-analysis-table {
    overflow-x: auto;
  }
  
  .table-header,
  .table-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  
  .methodology-grid,
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .tab-button {
    padding: 10px 16px;
    font-size: 0.9em;
  }
}
</style>
